/**
 * Notification settings card for the alias-wide Space defaults: the level every
 * uncustomised Space, category, and channel inherits, and the announcements and
 * Space changes toggles. Stored server-side per alias (unlike the device-local cards around it).
 */

import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SPACE_NOTIFY_LEVELS, SPACE_NOTIFY_TOGGLES } from '@adieuu/shared';
import { Card } from '../../components/Card';
import { useSpaceNotificationSettings } from '../../hooks/useSpaceNotificationSettings';
import '../../styles/_space-notifications-card.scss';

export const SpaceNotificationsCard = forwardRef<HTMLDivElement>(function SpaceNotificationsCard(_props, ref) {
  const { t } = useTranslation();
  const { available, defaults, setNotificationDefaults } = useSpaceNotificationSettings();
  if (!available) return null;

  return (
    <Card variant="elevated" className="slide-up app-settings-card" ref={ref} data-section="space-notifications">
      <h2 className="app-settings-section-title">{t('account.settings.notifications.spaces.sectionTitle')}</h2>
      <p className="app-settings-section-desc">{t('account.settings.notifications.spaces.sectionDescription')}</p>

      <fieldset className="space-notifications-level">
        <legend className="app-settings-toggle-title">{t('account.settings.notifications.spaces.levelLabel')}</legend>
        {SPACE_NOTIFY_LEVELS.map((level) => (
          <label key={level} className="app-settings-toggle">
            <input
              type="radio"
              name="space-notify-default"
              value={level}
              checked={defaults.level === level}
              onChange={() => {
                if (level !== defaults.level) void setNotificationDefaults({ ...defaults, level });
              }}
            />
            <span className="app-settings-toggle-label">
              <span className="app-settings-toggle-title">
                {t(`account.settings.notifications.spaces.levels.${level}`)}
              </span>
              <span className="app-settings-toggle-hint">
                {t(`account.settings.notifications.spaces.levelHints.${level}`)}
              </span>
            </span>
          </label>
        ))}
      </fieldset>

      {SPACE_NOTIFY_TOGGLES.map((toggle) => (
        <label key={toggle} className="app-settings-toggle">
          <input
            type="checkbox"
            checked={defaults[toggle]}
            onChange={(e) => void setNotificationDefaults({ ...defaults, [toggle]: e.target.checked })}
          />
          <span className="app-settings-toggle-label">
            <span className="app-settings-toggle-title">
              {t(`account.settings.notifications.spaces.${toggle}Toggle`)}
            </span>
            <span className="app-settings-toggle-hint">
              {t(`account.settings.notifications.spaces.${toggle}Hint`)}
            </span>
          </span>
        </label>
      ))}
    </Card>
  );
});
